/**
 * P&L cell formatting + heatmap shading — the single formatter for the
 * Sellerboard-style P&L surface (PnlColumnGrid / PnlProductsTable).
 *
 * Consolidates the money/pct/int/cost-as-red-negative helpers that were copied
 * across the old P&L surfaces. The hub's P&L page (PnlColumnGrid /
 * PnlProductsTable) imports from here; the standalone Products-page
 * Profitability tab (ProductPnlView) keeps its inline copy until that separate
 * route is migrated.
 *
 * NULL-tolerant by design: every formatter renders `null`/`undefined` as an em
 * dash so empty/partial date-range buckets and not-yet-fed gap metrics read as
 * "no data" rather than "$0" — they light up automatically as backfills land.
 */

/** `kind` from the backend PnlColumnRow — drives which formatter to use. */
export type PnlKind = 'int' | 'money' | 'pct' | 'cost'

/** Heatmap direction from the backend PnlColumnRow. */
export type PnlPolarity = 'good' | 'bad' | 'neutral'

const DASH = '—'

/** Whole-dollar USD with sign + thousands separators. Costs pass a negated
 *  value so they render as the negatives they are on a P&L. */
export function pnlMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return DASH
  return (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })
}

/** 0..1 fraction → integer percent (e.g. 0.214 → "21%"). */
export function pnlPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return DASH
  return `${(n * 100).toFixed(0)}%`
}

/** Integer with thousands separators. */
export function pnlInt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return DASH
  return Math.round(n).toLocaleString()
}

/** Cents-precision USD (e.g. per-unit COGS): "$12.34". Always positive-signed
 *  (per-unit costs read as a unit price, not a P&L line). */
export function pnlMoneyCents(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return DASH
  return '$' + n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}

/** Tailwind sign-color class for a money value (emerald ≥ 0, rose < 0), or
 *  empty when null. Mirrors the headline grid's net-profit coloring so the
 *  products table reads consistently. */
export function pnlSignClass(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return ''
  return n < 0 ? 'text-rose-600' : 'text-emerald-700'
}

/**
 * Format one P&L value by its row `kind`. `cost` is stored POSITIVE in the
 * backend (it is a cost) and rendered as the negative it represents.
 */
export function pnlFormat(kind: PnlKind, n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return DASH
  switch (kind) {
    case 'int':   return pnlInt(n)
    case 'pct':   return pnlPct(n)
    case 'cost':  return pnlMoney(-n)
    case 'money': return pnlMoney(n)
  }
}

/**
 * Per-row heatmap background for one cell.
 *
 * `rowMin`/`rowMax` are the min/max of the row's VISIBLE period values (Total
 * excluded — see PnlColumnGrid). Maps `value` to a green→amber→red tint:
 *   - polarity 'good': high = green, low = red
 *   - polarity 'bad'/cost: high = red, low = green (invert)
 *   - polarity 'neutral': no shading
 * Intensity ∝ |t − 0.5| so the median sits near-neutral and only the extremes
 * tint strongly. Returns a CSS `rgba()` string, or '' for no background.
 *
 * NULL-tolerant: a null value, a degenerate row (min==max), or a neutral
 * polarity all yield '' (plain cell) — empty buckets never get mis-shaded.
 */
export function heatColor(
  value: number | null | undefined,
  rowMin: number,
  rowMax: number,
  polarity: PnlPolarity,
): string {
  if (value == null || Number.isNaN(value)) return ''
  if (polarity === 'neutral') return ''
  if (rowMax === rowMin) return ''
  let t = (value - rowMin) / (rowMax - rowMin)
  t = Math.min(1, Math.max(0, t))
  // cost / "bad" rows: high is red → invert so the green end is the low end.
  if (polarity === 'bad') t = 1 - t
  // green (good) at t=1, red (bad) at t=0, amber in the middle.
  // Alpha ∝ distance from the neutral midpoint so the median reads near-clear.
  const alpha = Math.abs(t - 0.5) * 0.7   // 0 at median → ~0.35 at the extremes
  if (alpha < 0.02) return ''
  // 16,185,129 = emerald-600 ; 225,29,72 = rose-600.
  const [r, g, b] = t >= 0.5 ? [16, 185, 129] : [225, 29, 72]
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`
}
